/* 1f — booking-confirmation lightbox.
   Ported from `01-placements/1f_booking-confirmation-mobile-lightbox.html` in
   the Watercraft handoff pack. Colours are remapped onto the app palette in
   the homepage banner's `tokens.js`.

   The pack is explicit that this asset is STATIC: the copy is deliberately
   generic ("your dates", "all of your details") and must not be templated
   with the booking's venue, dates or swim — the session-created wording is
   written to cover every case. The caveat at the foot states both that the
   free pass is for new Watercraft users only and that the water and session
   are created either way; the pack says to keep it. */
import React from 'react';
import { Modal } from 'antd';
import ModalCloseCircle from '../../../assets/images/ModalCloseCircle';
import { usePreventScroll } from '../../../../../utils/hooks';
import {
  wcColors,
  wcSolidTint,
} from '../../../../home/components/watercraft/tokens';
import { ReactComponent as Wordmark } from '../../../../home/assets/images/watercraft/wc_wordmark.svg';
import '../../../assets/css/watercraft_session_modal.css';

const WATERCRAFT_URL = 'https://watercraft.app';

export default function WatercraftSessionModal({ open, onClose }) {
  // Matches SBPlusTrialModal: stop the confirmation page scrolling behind it.
  usePreventScroll('WatercraftSessionModal', open);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={'min(95%, 26rem)'}
      title={null}
      footer={null}
      closable={true}
      closeIcon={<ModalCloseCircle className='fs-45' />}
      maskClosable
      centered
      className='wc-1f-modal'
    >
      <div
        style={{
          width: '100%',
          position: 'relative',
          background: '#0C1A0F',
          overflow: 'hidden',
          boxSizing: 'border-box',
          boxShadow: '0 18px 40px rgba(18,35,46,.14)',
        }}
        data-capture='1f'
      >
        <div
          style={{
            position: 'absolute',
            left: '-110px',
            top: '-70px',
            width: '380px',
            height: '320px',
            borderRadius: '100%',
            background:
              'radial-gradient(circle,rgba(61,255,106,.3) 0%,rgba(61,255,106,.07) 48%,rgba(61,255,106,0) 72%)',
            filter: 'blur(40px)',
            pointerEvents: 'none',
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            left: '90px',
            bottom: '-140px',
            width: '340px',
            height: '300px',
            borderRadius: '100%',
            background:
              'radial-gradient(circle,rgba(61,255,106,.18) 0%,rgba(26,92,42,.09) 50%,rgba(61,255,106,0) 74%)',
            filter: 'blur(46px)',
            pointerEvents: 'none',
          }}
        ></div>

        <div style={{ position: 'relative', padding: '22px 18px 24px' }}>
          <div
            style={{
              position: 'relative',
              background:
                'linear-gradient(140deg,rgba(61,255,106,.13) 0%,rgba(61,255,106,.05) 42%,rgba(238,244,235,.03) 100%)',
              border: '1px solid rgba(61,255,106,.24)',
              borderRadius: '18px',
              padding: '24px 20px 22px',
              backdropFilter: 'blur(18px)',
              boxShadow:
                '0 20px 50px rgba(0,0,0,.35),inset 0 1px 0 rgba(238,244,235,.1)',
            }}
          >
            <span
              style={{
                position: 'absolute',
                top: '0',
                right: '22px',
                transform: 'translateY(-50%)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                background: wcSolidTint(wcColors.primary, 0.25),
                border: '1px solid rgba(61,255,106,.6)',
                color: '#3DFF6A',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '8.5px',
                fontWeight: '600',
                letterSpacing: '.2em',
                textTransform: 'uppercase',
                padding: '5px 11px',
                borderRadius: '100px',
                whiteSpace: 'nowrap',
                boxShadow: '0 4px 18px rgba(61,255,106,.22)',
              }}
            >
              <span
                style={{
                  width: '5px',
                  height: '5px',
                  borderRadius: '100px',
                  background: '#3DFF6A',
                  display: 'block',
                }}
              ></span>
              Included with this booking
            </span>

            <Wordmark
              role='img'
              aria-label='Watercraft'
              color={wcColors.textPrimary}
              style={{
                width: '66px',
                height: '42px',
                display: 'block',
                marginBottom: '16px',
              }}
            />
            <h2
              style={{
                fontFamily: "'Playfair Display',Georgia,serif",
                fontSize: '30px',
                lineHeight: '1.06',
                fontWeight: '600',
                color: '#EEF4EB',
                margin: '0 0 12px',
                letterSpacing: '-.015em',
              }}
            >
              Your session is already
              <span
                style={{
                  display: 'block',
                  fontStyle: 'italic',
                  fontWeight: '400',
                  color: '#D6E2D2',
                }}
              >
                waiting in Watercraft.
              </span>
            </h2>
            <p
              style={{
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '14px',
                lineHeight: '1.6',
                color: '#9AB49B',
                fontWeight: '300',
                margin: '0 0 18px',
              }}
            >
              We've created this booking as a session in the Watercraft app,
              with the water and the dates already filled in.
            </p>

            <div
              style={{
                background: 'rgba(12,26,15,.55)',
                border: '1px solid #2A4030',
                borderRadius: '12px',
                padding: '13px 14px',
                marginBottom: '16px',
              }}
            >
              <div
                style={{
                  fontFamily: "'JetBrains Mono',monospace",
                  fontSize: '9px',
                  letterSpacing: '.16em',
                  textTransform: 'uppercase',
                  color: '#6E8270',
                  marginBottom: '7px',
                }}
              >
                Your session
              </div>
              <div
                style={{
                  fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                  fontSize: '15px',
                  fontWeight: '600',
                  color: '#EEF4EB',
                  marginBottom: '4px',
                }}
              >
                Session created with all of your details
              </div>
              <div
                style={{
                  fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                  fontSize: '12.5px',
                  color: '#9AB49B',
                  fontWeight: '300',
                }}
              >
                The water and your dates, already filled in
              </div>
            </div>

            <div
              style={{
                background: 'rgba(61,255,106,.07)',
                border: '1px solid rgba(61,255,106,.45)',
                borderRadius: '12px',
                padding: '14px',
                marginBottom: '18px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '9px',
                  marginBottom: '6px',
                }}
              >
                <span
                  style={{
                    fontFamily: "'JetBrains Mono',monospace",
                    fontSize: '9px',
                    letterSpacing: '.16em',
                    textTransform: 'uppercase',
                    color: '#3DFF6A',
                    fontWeight: '600',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Free session pass
                </span>
                <span
                  style={{
                    flex: '1',
                    height: '1px',
                    background: 'rgba(61,255,106,.25)',
                    display: 'block',
                  }}
                ></span>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono',monospace",
                    fontSize: '11px',
                    color: '#3DFF6A',
                  }}
                >
                  ×1
                </span>
              </div>
              <div
                style={{
                  fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                  fontSize: '13px',
                  lineHeight: '1.55',
                  color: '#D6E2D2',
                  fontWeight: '300',
                }}
              >
                Redeem it against this booking to unlock the unlimited version
                of Watercraft for the whole session. No card, nothing to cancel.
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '11px',
                marginBottom: '20px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  gap: '11px',
                  alignItems: 'flex-start',
                }}
              >
                <span
                  style={{
                    flex: 'none',
                    width: '19px',
                    height: '19px',
                    borderRadius: '100px',
                    border: '1px solid rgba(61,255,106,.5)',
                    color: '#3DFF6A',
                    fontFamily: "'JetBrains Mono',monospace",
                    fontSize: '9.5px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginTop: '1px',
                  }}
                >
                  1
                </span>
                <span
                  style={{
                    fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                    fontSize: '13px',
                    lineHeight: '1.5',
                    color: '#D6E2D2',
                    fontWeight: '300',
                  }}
                >
                  Download the app.
                </span>
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: '11px',
                  alignItems: 'flex-start',
                }}
              >
                <span
                  style={{
                    flex: 'none',
                    width: '19px',
                    height: '19px',
                    borderRadius: '100px',
                    border: '1px solid rgba(61,255,106,.5)',
                    color: '#3DFF6A',
                    fontFamily: "'JetBrains Mono',monospace",
                    fontSize: '9.5px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginTop: '1px',
                  }}
                >
                  2
                </span>
                <span
                  style={{
                    fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                    fontSize: '13px',
                    lineHeight: '1.5',
                    color: '#D6E2D2',
                    fontWeight: '300',
                  }}
                >
                  Log in with the same swimbooker details you've just booked
                  with.
                </span>
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: '11px',
                  alignItems: 'flex-start',
                }}
              >
                <span
                  style={{
                    flex: 'none',
                    width: '19px',
                    height: '19px',
                    borderRadius: '100px',
                    border: '1px solid rgba(61,255,106,.5)',
                    color: '#3DFF6A',
                    fontFamily: "'JetBrains Mono',monospace",
                    fontSize: '9.5px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginTop: '1px',
                  }}
                >
                  3
                </span>
                <span
                  style={{
                    fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                    fontSize: '13px',
                    lineHeight: '1.5',
                    color: '#D6E2D2',
                    fontWeight: '300',
                  }}
                >
                  Your session is sat there ready. Start logging catches, bait,
                  swims and conditions as they happen, and it works out the
                  pattern while you're still on the bank.
                </span>
              </div>
            </div>

            <a
              className='wc-1f__cta'
              href={WATERCRAFT_URL}
              target='_blank'
              rel='noopener noreferrer'
              style={{
                display: 'block',
                textAlign: 'center',
                background: '#3DFF6A',
                color: '#0C1A0F',
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '15px',
                fontWeight: '700',
                padding: '15px',
                borderRadius: '11px',
                whiteSpace: 'nowrap',
                marginBottom: '16px',
              }}
            >
              Claim your free session pass
            </a>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '22px',
                flexWrap: 'wrap',
              }}
            >
              <a
                className='wc-1f__store'
                href={WATERCRAFT_URL}
                target='_blank'
                rel='noopener noreferrer'
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  whiteSpace: 'nowrap',
                }}
              >
                <svg
                  viewBox='0 0 24 24'
                  style={{ width: '13px', height: '16px', display: 'block' }}
                  fill='#EEF4EB'
                  aria-hidden='true'
                >
                  <path d='M17.05 12.53c-.02-2.2 1.79-3.26 1.87-3.31-1.02-1.49-2.61-1.7-3.17-1.72-1.35-.14-2.63.79-3.31.79-.68 0-1.79-.77-2.94-.75-1.51.02-2.9.88-3.68 2.23-1.57 2.72-.4 6.74 1.13 8.95.75 1.08 1.64 2.24 2.81 2.2 1.13-.04 1.56-.73 2.93-.73 1.36 0 1.75.73 2.94.71 1.21-.02 1.98-1.1 2.72-2.19.85-1.25 1.2-2.46 1.22-2.52-.03-.01-2.34-.9-2.36-3.56z' />
                  <path d='M14.9 6.02c.6-.72 1-1.72.89-2.72-.86.03-1.9.57-2.52 1.29-.55.63-1.03 1.65-.9 2.62.96.07 1.93-.49 2.53-1.19z' />
                </svg>
                <span
                  style={{
                    fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                    fontSize: '12.5px',
                    fontWeight: '500',
                    color: '#EEF4EB',
                  }}
                >
                  App Store
                </span>
              </a>
              <a
                className='wc-1f__store'
                href={WATERCRAFT_URL}
                target='_blank'
                rel='noopener noreferrer'
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  whiteSpace: 'nowrap',
                }}
              >
                <svg
                  viewBox='0 0 24 24'
                  style={{ width: '13px', height: '16px', display: 'block' }}
                  aria-hidden='true'
                >
                  <path
                    d='M4 2.4 L18.6 12 L4 21.6 Z'
                    fill='none'
                    stroke='#EEF4EB'
                    strokeWidth='1.8'
                    strokeLinejoin='round'
                  />
                </svg>
                <span
                  style={{
                    fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                    fontSize: '12.5px',
                    fontWeight: '500',
                    color: '#EEF4EB',
                  }}
                >
                  Google Play
                </span>
              </a>
            </div>
            <p
              style={{
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '11.5px',
                lineHeight: '1.55',
                color: '#6E8270',
                fontWeight: '300',
                margin: '16px 0 0',
                paddingTop: '14px',
                borderTop: '1px solid rgba(61,255,106,.14)',
              }}
            >
              The free session pass is for new Watercraft users only. Either
              way, we'll always create the water and this session in your
              Watercraft account if they aren't there already.
            </p>
          </div>
        </div>

        <div style={{ position: 'relative', padding: '0 18px 26px' }}>
          <div
            style={{
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: '9px',
              letterSpacing: '.16em',
              textTransform: 'uppercase',
              color: '#6E8270',
              marginBottom: '11px',
            }}
          >
            Already loaded for your dates
          </div>
          <div
            style={{
              background: '#162419',
              border: '1px solid #2A4030',
              borderRadius: '14px',
              padding: '16px',
              boxShadow: '0 18px 36px rgba(0,0,0,.5)',
            }}
          >
            <div
              style={{
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '18px',
                fontWeight: '700',
                color: '#EEF4EB',
                letterSpacing: '-.015em',
                marginBottom: '6px',
              }}
            >
              Solunar and conditions
            </div>
            <div
              style={{
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '12.5px',
                lineHeight: '1.5',
                color: '#9AB49B',
                fontWeight: '300',
              }}
            >
              When the fish are most likely to be feeding, and what the weather
              is doing across your session. Open the app and it's already there
              for this water.
            </div>
            <div style={{ display: 'flex', gap: '7px', margin: '14px 0 13px' }}>
              <div
                style={{
                  flex: '1',
                  background: '#1E3024',
                  border: '1px solid #2E8C47',
                  borderRadius: '9px',
                  padding: '9px 10px',
                }}
              >
                <div
                  style={{
                    fontFamily: "'JetBrains Mono',monospace",
                    fontSize: '8.5px',
                    letterSpacing: '.14em',
                    textTransform: 'uppercase',
                    color: '#3DFF6A',
                    marginBottom: '4px',
                  }}
                >
                  Major
                </div>
                <div
                  style={{
                    fontFamily: "'JetBrains Mono',monospace",
                    fontSize: '13px',
                    color: '#EEF4EB',
                  }}
                >
                  05:14
                </div>
              </div>
              <div
                style={{
                  flex: '1',
                  background: '#1E3024',
                  border: '1px solid #2E8C47',
                  borderRadius: '9px',
                  padding: '9px 10px',
                }}
              >
                <div
                  style={{
                    fontFamily: "'JetBrains Mono',monospace",
                    fontSize: '8.5px',
                    letterSpacing: '.14em',
                    textTransform: 'uppercase',
                    color: '#3DFF6A',
                    marginBottom: '4px',
                  }}
                >
                  Major
                </div>
                <div
                  style={{
                    fontFamily: "'JetBrains Mono',monospace",
                    fontSize: '13px',
                    color: '#EEF4EB',
                  }}
                >
                  17:39
                </div>
              </div>
              <div
                style={{
                  flex: '1',
                  background: '#162419',
                  border: '1px solid #2A4030',
                  borderRadius: '9px',
                  padding: '9px 10px',
                }}
              >
                <div
                  style={{
                    fontFamily: "'JetBrains Mono',monospace",
                    fontSize: '8.5px',
                    letterSpacing: '.14em',
                    textTransform: 'uppercase',
                    color: '#6E8270',
                    marginBottom: '4px',
                  }}
                >
                  Minor
                </div>
                <div
                  style={{
                    fontFamily: "'JetBrains Mono',monospace",
                    fontSize: '13px',
                    color: '#D6E2D2',
                  }}
                >
                  11:02
                </div>
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                gap: '12px',
                alignItems: 'center',
                paddingTop: '13px',
                borderTop: '1px solid #2A4030',
              }}
            >
              <div style={{ flex: 'none' }}>
                <div
                  style={{
                    fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                    fontSize: '19px',
                    fontWeight: '700',
                    color: '#EEF4EB',
                    lineHeight: '1',
                  }}
                >
                  1011
                  <span style={{ fontSize: '11px', color: '#3B9EBF' }}>↓</span>
                </div>
                <div
                  style={{
                    fontFamily: "'JetBrains Mono',monospace",
                    fontSize: '9px',
                    letterSpacing: '.1em',
                    color: '#6E8270',
                    marginTop: '5px',
                  }}
                >
                  PRESSURE
                </div>
              </div>
              <svg
                viewBox='0 0 160 46'
                style={{ flex: '1', display: 'block', height: '46px' }}
              >
                <path
                  d='M2 40 C 12 40, 16 14, 26 14 C 36 14, 40 38, 52 38 C 70 38, 76 30, 86 26 C 96 22, 102 30, 112 36 C 122 40, 128 12, 140 12 C 152 12, 156 34, 158 38'
                  fill='none'
                  stroke='#3DFF6A'
                  strokeWidth='2.2'
                  strokeLinecap='round'
                />
                <line
                  x1='112'
                  y1='2'
                  x2='112'
                  y2='42'
                  stroke='#F5A623'
                  strokeWidth='1.3'
                  strokeDasharray='3 3'
                />
              </svg>
              <div style={{ flex: 'none', textAlign: 'right' }}>
                <div
                  style={{
                    fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                    fontSize: '19px',
                    fontWeight: '700',
                    color: '#EEF4EB',
                    lineHeight: '1',
                  }}
                >
                  SW 8
                </div>
                <div
                  style={{
                    fontFamily: "'JetBrains Mono',monospace",
                    fontSize: '9px',
                    letterSpacing: '.1em',
                    color: '#6E8270',
                    marginTop: '5px',
                  }}
                >
                  WIND MPH
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
